import React, { useContext, useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import { Wizard, Steps, Step } from 'react-albus';


import BackButton from '../custom/BackButton';
import ActionIcon from '../custom/ActionIcon';
import { createdUpdatedInfo, getRandomNum, getTsDate } from '../../utils';
import { atom, useRecoilState, useRecoilValue } from 'recoil';
import TopNavigation from '../wizard-hooks/TopNavigation';
import { FormGroup } from 'reactstrap';
import { Input } from 'reactstrap';
import { ButtonGroup } from 'reactstrap';
import { Button } from 'reactstrap';
import { IssueReport, IssueReportTypes, IStep } from '../../globals/interfaces';
import BottomNavigation from '../wizard-hooks/BottomNavigation';
import { AppContext } from '../../context';
import RenderAccess from './RenderAccess';
import { useZodForm, ZodFormProvider } from '../custom/ZodForm/ZodFormProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { Schema } from './validation';
import useUser from '../../customHooks/useUser';
import { createZodInput } from '../custom/ZodForm/TypedInput';
import RenderButtonGroupBoolean from '../RenderButtonGroup/RenderButtonGroupBoolean';
import apiClient from '../../apiClient';
import useLoading from '../../customHooks/useLoading';
import { MoonLoader } from 'react-spinners';
import { successMessages } from './constants';
import success from '../../assets/lottie/success.json';
import Lottie from 'lottie-react';
import moment from 'moment';
import DragAndDropFileUpload from '../DragDropUpload';

const TYPES: Array<IssueReportTypes> = ['EOS', 'IT'];

export const bugReportModalState = atom<boolean>({
    key: 'bugReportModal',
    default: false
});

export const bugReportScreenshotState = atom<string>({
    key: 'bugReportScreenshot',
    default: ''
});

const FormInput = createZodInput<IssueReport>();

export default function IssueReporting() {

    const { user } = useUser();
    const { accessMap } = useContext(AppContext);
    const { loading, setLoading } = useLoading();
    const [modal, setModal] = useRecoilState(bugReportModalState);
    const [bugOnPage, setBugOnPage] = useState(undefined);
    const capturedScreenshot = useRecoilValue(bugReportScreenshotState);
    const [uploadImage, setUploadImage] = useState<string>();

    const methods = useZodForm<IssueReport>({
        resolver: zodResolver(Schema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        context: {
            valueAsNumber: true
        }
    });

    const {
        handleSubmit,
        reset,
        clearErrors,
        trigger,
        formState: { errors, isValid, isDirty },
        requiredFields,
        setValue,
        watch,
        getValues
    } = methods;

    const type = watch('type');
    const details = watch('details');
    const completeTask = watch('completeTask');
    const numAffected = watch('numAffected');
    const startDate = watch('startDate');

    useEffect(() => {
        if (modal) {
            // Clear all previous errors
            clearErrors();
            const now = getTsDate();

            reset({
                details: '',
                completeTask: undefined,
                numAffected: 1,
                startDate: '',
                page: window.location.pathname,
                screenshot: undefined,
                unit: user.s_unit,
                createdAt: now,
                createdBy: user.s_email,
                type: undefined
            });

            trigger();
            setBugOnPage(undefined);
        }
    }, [modal, user, reset, clearErrors]);

    const toggle = () => setModal(prev => !prev);

    const setCompleteTask = (value: boolean) => {
        setValue('completeTask', value);
    }

    const submitIssue = async (data: IssueReport) => {
        data.startDate = moment(data.startDate).format('YYYY-MM-DD HH:mm');
        setLoading(true);
        await apiClient.post(`/issue-report`, data);
        setLoading(false);
        setTimeout(() => {
            setModal(false);
        }, 10000);
    }

    const onClickNext = async (goToNext: () => void, steps: Array<IStep>, step: IStep) => {
        if (step.id === 'Start') {
            setValue('screenshot', type === 'EOS' ? capturedScreenshot : uploadImage);
            handleSubmit(
                async (data) => await submitIssue(data),
                (errors) => console.log('validation failed:', errors)
            )();
        }
        step.isDone = true;
        goToNext();
    };

    const onClickPrev = (goToPrev: () => void, steps: Array<IStep>, step: IStep) => {
        if (steps.indexOf(step) <= 0) {
            return;
        }
        goToPrev();
    };

    const resolveEnableNext = (step: IStep) => {
        if (step.id === 'Welcome') {
            return Boolean(type);
        } if (step.id === 'Page') {
            if (type === 'EOS') {
                return bugOnPage === true;
            }
            return Boolean(uploadImage);
        } else if (step.id === 'Details') {
            if (!details || details.length < 11) {
                return false;
            }
        } else if (step.id === 'Task') {
            return completeTask !== undefined;
        } else if (step.id === 'Number') {
            return numAffected !== undefined;
        } else if (step.id === 'Start') {
            return startDate && moment(startDate).isValid();
        }
        return true;
    };

    const resolveEnablePrev = (step: IStep) => {
        if (step.id === 'Complete') {
            return false;
        }
        return true;
    };

    const selectNow = () => {
        setValue('startDate', moment().format('YYYY-MM-DDTHH:mm'));
    }

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>EOS Issue Report {type}</h4>
                </HeaderContainer>
            </ModalHeader>
            <CustomModalBody>
                <Wizard>
                    <div className="wizard wizard-default mt-1 text-center">
                        <ZodFormProvider methods={methods} requiredFields={requiredFields}>
                            <TopNavigation
                                className="justify-content-center mt-0"
                                disableNav={true}
                                topNavClick={() => { }}
                            />
                            <Steps>
                                <Step id={'Welcome'} name={'Welcome'}>
                                    <CenteredDiv>
                                        <h6>Welcome to <b>EOS</b> Issue report.</h6>
                                        <h6>Please use this page for EOS or IT related issues.</h6>
                                        <h6>Before you continue, please ensure you have taken the following troubleshooting steps:</h6>
                                        <List>
                                            <li>Refresh EOS</li>
                                            <li>Reboot your computer</li>
                                            <li>Advise and check with your supervisor</li>
                                        </List>
                                        <h6>Please select what type of issue your're having:</h6>
                                        <ButtonGroup>
                                            {
                                                TYPES.map((item, i) => (
                                                    <Button
                                                        key={i}
                                                        onClick={() => setValue('type', item)}
                                                        active={type === item}
                                                    >
                                                        {item}
                                                    </Button>
                                                ))
                                            }
                                        </ButtonGroup>
                                    </CenteredDiv>
                                </Step>
                                <Step id={'Page'} name={'Page'}>
                                    {
                                        type === 'EOS' && (
                                            <div>
                                                <h6>Current Screenshot</h6>
                                                <Screenshot src={capturedScreenshot} />
                                                <h6>To assist with troubleshooting, please remain on the affected page. Is your issue on this current page?</h6>
                                                <div>
                                                    <RenderButtonGroupBoolean
                                                        value={bugOnPage}
                                                        setValue={setBugOnPage}
                                                    />
                                                    {
                                                        bugOnPage === false && <RenderAccess accessMap={accessMap} />
                                                    }
                                                </div>
                                            </div>
                                        ) || (
                                            <DragAndDropContainer>
                                                <DragAndDropFileUpload
                                                    onDone={file => setUploadImage(file.base64)}
                                                    autoCrop={false}
                                                    allowManualCrop={false}
                                                    previewMaxHeight={200}
                                                />
                                            </DragAndDropContainer>
                                        )
                                    }
                                </Step>
                                <Step id={'Details'} name={'Details'}>
                                    <CenteredDiv>
                                        <h6>Describe your <b>EOS</b> issue in detail:</h6>
                                        <DetailsInput type={'textarea'} name={'details'} label={''} style={{ width: '500px' }} />
                                    </CenteredDiv>
                                </Step>
                                <Step id={'Task'} name={'Task'}>
                                    <CenteredDiv>
                                        <h6>Can you still complete your task?</h6>
                                        <RenderButtonGroupBoolean
                                            value={completeTask}
                                            setValue={setCompleteTask}
                                        />
                                    </CenteredDiv>
                                </Step>
                                <Step id={'Number'} name={'Number affected'}>
                                    <CenteredDiv>
                                        <h6>How many people are affected?</h6>
                                        <NumberInput type={'number'} name={'numAffected'} label={''} style={{ width: '100px' }} />
                                    </CenteredDiv>
                                </Step>
                                <Step id={'Start'} name={'Start date'}>
                                    <CenteredDiv>
                                        <h6>When did this start?</h6>
                                        <Button className={'mt-2'} onClick={selectNow}>Now</Button>
                                        <h6 className={'mt-2 mb-0'}>or</h6>
                                        <DateInput type={'datetime-local'} name={'startDate'} label={''} style={{ width: '250px' }} />
                                    </CenteredDiv>
                                </Step>
                                <Step id={'Complete'} name={'Complete'}>
                                    <CenteredDiv>
                                        {
                                            loading ? (
                                                <MoonLoader
                                                    size={28}
                                                    color={'#51c878'}
                                                />
                                            ) : (
                                                <CenteredDiv>
                                                    <SuccessContainer>
                                                        <Lottie
                                                            animationData={success}
                                                            loop={false}
                                                        />
                                                    </SuccessContainer>
                                                    <h6>{successMessages[getRandomNum(0, successMessages.length - 1)]}</h6>
                                                    <h6>We will reach out to you via MS Teams.</h6>
                                                    <Button onClick={toggle}>EXIT</Button>
                                                </CenteredDiv>
                                            )
                                        }
                                    </CenteredDiv>
                                </Step>
                            </Steps>
                            <BottomNavigation
                                onClickNext={onClickNext}
                                onClickPrev={onClickPrev}
                                className="justify-content-center"
                                prevLabel={'Back'}
                                nextLabel={'Next'}
                                resolveEnableNext={resolveEnableNext}
                                resolveEnablePrev={resolveEnablePrev}
                                hideOnStepId={'Complete'}
                            />
                        </ZodFormProvider>
                    </div>
                </Wizard>
            </CustomModalBody>
        </CustomModal>
    );
}

const CustomModal = styled(Modal)`
    max-width: 1000px;
`;

const CustomModalBody = styled(ModalBody)`
    height: 600px;
`;

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const Screenshot = styled.img`
    object-fit: contain;
    max-height: 300px; /* Optional: Prevents it from getting too large on desktops */
    height: auto;
    border: 1px solid black;

    @media (max-width: 950px) {
        width: 100%; /* Scales to full width on tablets and phones */
    }
`;

const CenteredDiv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const List = styled.ul`
    text-align: left;

    li {
        font-size: 16px;
    }
`;

const DetailsInput = styled(FormInput)`
    width: 500px !important;
`;

const NumberInput = styled(FormInput)`
    width: 100px;
`;

const DateInput = styled(FormInput)`
    width: 150px;
`;

const SuccessContainer = styled.div`
    height: 100px;
    width: 100px;
`;

const DragAndDropContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;